import { PopupWithApiItem } from "@risalabs_frontend_org/oasis-ui-kit";

/**
 * Biosimilar drug family: generic reference name and interchangeable product labels.
 */
interface BiosimilarDrugFamily {
  /** Reference generic or class name (e.g. Bevacizumab). */
  genericName: string;
  /** Interchangeable biosimilar / reference products shown in the retry popup. */
  alternatives: string[];
}

/**
 * Biosimilar interchange groups used to populate retry popup options by row drug name.
 */
export const BIOSIMILAR_DRUG_FAMILIES: BiosimilarDrugFamily[] = [
  {
    genericName: "Aranesp / Procrit",
    alternatives: ["Aranesp", "Procrit"],
  },
  {
    genericName: "Bevacizumab",
    alternatives: ["Zirabev", "Mvasi", "Vegzelma", "Avastin", "Alymsys"],
  },
  {
    genericName: "Denosumab",
    alternatives: ["Prolia", "Jubbonti (60 mg)", "Xgeva", "Wyost (120 mg)"],
  },
  {
    genericName: "Filgrastim",
    alternatives: ["Granix", "Zarxio", "Neupogen", "Nivestym"],
  },
  {
    genericName: "Infliximab",
    alternatives: ["Remicade", "Inflectra", "Avsola", "Renflexis"],
  },
  {
    genericName: "IVIG",
    alternatives: ["Gammagard", "Octagam", "Gamunex", "Privigen"],
  },
  {
    genericName: "LHRH",
    alternatives: ["Lupron", "Eligard"],
  },
  {
    genericName: "Peg-Filgrastim",
    alternatives: ["Udenyca", "Neulasta", "Nyvepria", "Rolvedon", "Fulphila"],
  },
  {
    genericName: "RituXan",
    alternatives: ["Ruxience", "RituXan", "Riabni", "Truxima"],
  },
  {
    genericName: "Trastuzumab",
    alternatives: [
      "Trazimera",
      "Ontruzant",
      "Herceptin",
      "Hercessi",
      "Kanjinti",
      "Ogivri",
      "Herzuma",
    ],
  },
];

/**
 * Extracts the leading drug name from a row value before strength, units, or dosage text.
 * E.g. "Procrit 40,000 Units" → "Procrit", "Zirabev 25MG/ML" → "Zirabev".
 *
 * @param value - Raw drug label from the oncology submission table row
 * @returns First whitespace-delimited token from the row drug string
 */
const extractRowDrugPrimaryName = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }
  return (trimmed.split(/\s+/)[0] ?? "").trim();
};

/**
 * Normalizes a drug label for family matching (case/punctuation insensitive).
 *
 * @param value - Drug name or mapping label
 * @returns Lowercase alphanumeric token used for comparisons
 */
const normalizeDrugLabelForMatch = (value: string): string =>
  value
    .trim()
    .split(/\s+/)[0]
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

/**
 * Builds a stable checkbox id for a biosimilar popup option.
 *
 * @param label - Display label shown in the popup
 * @returns Slug id for `PopupWithApi` checkbox state
 */
const toBiosimilarPopupItemId = (label: string): string =>
  normalizeDrugLabelForMatch(label) || "biosimilar-option";

/**
 * Returns whether a row drug matches a family generic name or one of its alternatives.
 * Only the first word of the row drug is used (e.g. "Procrit 40,000 Units" → "Procrit").
 *
 * @param rowDrug - Drug value from the oncology submission row
 * @param family - Biosimilar family definition
 * @returns True when the row drug belongs to the family
 */
const rowDrugMatchesFamily = (
  rowDrug: string,
  family: BiosimilarDrugFamily,
): boolean => {
  const rowPrimaryName = extractRowDrugPrimaryName(rowDrug);
  const normalizedRowDrug = normalizeDrugLabelForMatch(rowPrimaryName);
  if (!normalizedRowDrug) {
    return false;
  }

  const candidates = [family.genericName, ...family.alternatives];
  return candidates.some((candidate) => {
    const normalizedCandidate = normalizeDrugLabelForMatch(candidate);
    if (!normalizedCandidate) {
      return false;
    }
    return normalizedRowDrug === normalizedCandidate;
  });
};

/**
 * Resolves the biosimilar family for a table row drug name.
 *
 * @param drugName - Drug column value from the order row
 * @returns Matching family, or null when no mapping exists
 */
export const findBiosimilarFamilyForDrug = (
  drugName: string | null | undefined,
): BiosimilarDrugFamily | null => {
  const trimmedDrug = String(drugName ?? "").trim();
  if (!trimmedDrug) {
    return null;
  }

  return (
    BIOSIMILAR_DRUG_FAMILIES.find((family) =>
      rowDrugMatchesFamily(trimmedDrug, family),
    ) ?? null
  );
};

/**
 * Builds `PopupWithApi` checkbox items for the biosimilar retry popup from the row drug.
 *
 * @param drugName - Drug column value from the order row
 * @returns Popup checklist items for the matched biosimilar family
 */
export const getBiosimilarPopupItemsForDrug = (
  drugName: string | null | undefined,
): PopupWithApiItem[] => {
  const family = findBiosimilarFamilyForDrug(drugName);
  if (!family) {
    return [];
  }

  return family.alternatives.map((label) => ({
    id: toBiosimilarPopupItemId(label),
    label,
  }));
};

/**
 * Resolves the popup group label shown above biosimilar retry checkboxes.
 *
 * @param drugName - Drug column value from the order row
 * @returns Label for the popup checkbox group
 */
export const getBiosimilarPopupGroupLabel = (
  drugName: string | null | undefined,
): string => {
  const family = findBiosimilarFamilyForDrug(drugName);
  if (!family) {
    return "Select biosimilar option";
  }

  return `Select biosimilar option for ${family.genericName}`;
};
